import { useEffect, useRef, useState } from 'react';
import { cbColor } from '../lib/a11y';
import L from 'leaflet';
import { PIPE_TYPES, FITTING_TYPES, FIT_STATES, pipeLatLngs, pathLength } from '../lib/model';
import { MAINT, maintMatch } from '../lib/maint';
import { MED_COLOR } from '../lib/catalog';
import { openSurvey } from './SurveyDialog';
import { redText, autoAngle } from './FittingExtras';
import { isRot, fitArms, symbolSvg } from '../lib/fitSymbols';
import { pipeSlope, fmtPm } from '../lib/sewer';

export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
export const safeColor = (c, d = '#ff6a13') => (/^#[0-9a-f]{3,8}$/i.test(String(c || '')) ? c : d);
const safeKey = (k) => String(k ?? '').replace(/[^\w-]/g, '');

// maintenance status overrides the catalogue colour (live traffic light)
export const fitColor = (f) => cbColor(safeColor(f.maint?.status ? MAINT[f.maint.status] : f.color || FITTING_TYPES[f.type] || FITTING_TYPES.other));

const rawBar = (f) => (f.pIst === '' || f.pIst == null || !Number.isFinite(+f.pIst) ? null : +f.pIst);
// computed (elevation-corrected) pressure, else the measured value flagged as uncorrected
function pressBadge(t, f, pr, id) {
  if (pr) return `<i class="pr-badge pr-${pr.leak ? 'leak' : pr.status}" data-testid="pressure-badge-${id}">${pr.bar.toFixed(1).replace('.', ',')}</i>`;
  const v = rawBar(f);
  return v == null ? '' : `<i class="pr-badge pr-raw" title="${esc(t.prRawHint)}" data-testid="pressure-badge-${id}">${v.toFixed(1).replace('.', ',')} bar · ${esc(t.prRawShort)}</i>`;
}

export function fittingHtml(t, f, pr, ang, arms) {
  const sv = symbolSvg(f, { ang, arms });
  const id = safeKey(f.id), ty = safeKey(f.type);
  const pb = pressBadge(t, f, pr, id);
  if (sv) return pb ? `<span class="fit-svw">${sv}${pb}</span>` : sv;
  if (f.type === 'gg' || f.type === 'gk') return `<span class="fit-bnd fit-bnd-${ty}" data-testid="map-fitting-${id}">|${ty.toUpperCase()}|</span>`;
  const st = f.state && f.state !== 'none' ? `<i class="fit-state" style="background:${safeColor(FIT_STATES[f.state])}"></i>` : '';
  const mt = (f.maint?.status ? ` mt-ring" data-maint="${safeKey(f.maint.status)}" style="--mt:${MAINT[f.maint.status]};` : '" style="') + (f.angle ? `--ang:calc(${+f.angle}deg + var(--brg, 0deg));` : '');
  const pu = f.privUnknown ? `<i class="fit-priv" data-testid="priv-badge-${id}">⚠️</i>` : '';
  const wi = f.winter ? `<i class="wi-badge wi-${f.winter === 'done' ? 'done' : 'pend'}">❄</i>` : '';
  const md = f.med && MED_COLOR[f.med] ? `<i class="med-b" style="background:${MED_COLOR[f.med]}" data-testid="med-badge-${id}"></i>` : '';
  const dr = f.draft ? `<i class="fit-draft-b" data-testid="draft-badge-${id}">${esc(t.prDraftAb)}</i>` : '';
  return `<span class="fit-sym fit-${ty}${f.draft ? ' fit-draft' : ''}${mt}background:${fitColor(f)}" data-testid="map-fitting-${id}"${(f.sym || '').length > 2 ? ' data-long="1"' : ''}>${esc(f.sym || t['fab_' + f.type])}${st}${pu}${pb}${wi}${md}${dr}</span>`;
}

export function nearestOnPipes(map, latlng, pipes, fittings, tolPx = 16, onlyId = null) {
  const p = map.latLngToLayerPoint(latlng);
  let best = null;
  pipes.forEach((pp) => {
    if (onlyId && pp.id !== onlyId) return;
    const pts = pp.points.map((pt) => (Array.isArray(pt) ? pt : (() => { const f = fittings.find((x) => x.id === pt.fid); return f ? [f.lat, f.lon] : null; })()));
    for (let k = 0; k < pts.length - 1; k++) {
      if (!pts[k] || !pts[k + 1]) continue;
      const a = map.latLngToLayerPoint(pts[k]), b = map.latLngToLayerPoint(pts[k + 1]);
      const c = L.LineUtil.closestPointOnSegment(p, a, b);
      const d = c.distanceTo(p);
      if (d <= tolPx && (!best || d < best.d)) {
        const ll = map.layerPointToLatLng(c);
        best = { pipeId: pp.id, k, d, pt: [ll.lat, ll.lng] };
      }
    }
  });
  return best;
}

// flow arrows (▸) along a sewer reach + slope badge
function sewerMarks(g, pp, pts, live) {
  const s = pipeSlope(pp, live);
  const st = s?.status || 'none';
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], dx = (b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180), dy = b[0] - a[0];
    const len = Math.hypot(dx * 111320, dy * 110540), n = Math.max(1, Math.floor(len / 25)), deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
    for (let k = 1; k <= n; k++) {
      const u = k / (n + 1);
      g.addLayer(L.marker([a[0] + dy * u, a[1] + (b[1] - a[1]) * u], { pane: 'pipes', interactive: false, icon: L.divIcon({ className: 'sw-arrow-wrap', html: `<span class="sw-arrow sw-${st}" style="transform:rotate(${deg.toFixed(1)}deg)">▸</span>`, iconSize: [16, 16], iconAnchor: [8, 8] }) }));
    }
  }
  if (s?.pm != null) g.addLayer(L.marker(pathMid(pts), { interactive: false, zIndexOffset: 300, icon: L.divIcon({ className: 'sw-badge-wrap', html: `<span class="sw-badge sw-${st}" data-testid="slope-badge-${safeKey(pp.id)}">${st === 'neg' ? '⛔ ' : st === 'low' ? '⚠️ ' : ''}${fmtPm(s.pm)}</span>`, iconSize: [0, 0] }) }));
}

const pathMid = (pts) => pts[Math.floor(pts.length / 2) - (pts.length % 2 ? 0 : 1)].map((v, k) => (pts.length % 2 ? v : (v + pts[pts.length / 2][k]) / 2));

export default function useNetworkLayers(mapRef, ready, { t, pipes, fittings, draft, netUnlocked, fitFilter, maintFilter, onFittingClick, onMoveFitting, onMoveVertex, onLineClick, onDeleteVertex, selId, onPipeClick, onMovePipe, press, symStyle }) {
  const groupRef = useRef(null);
  const cb = useRef({});
  // zoom level bucket: in large networks small objects (taps, houses) are drawn light-weight or only when zoomed in
  const [zb, setZb] = useState(0), [vk, setVk] = useState('');
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    // zoomed in: the view key changes after panning, so only the objects in view (+ margin) are drawn
    const b = () => { const z = map.getZoom(); setZb(z >= 18 ? 2 : z >= 16 ? 1 : 0); const c = map.getCenter(), bb = map.getBounds(), sy = (bb.getNorth() - bb.getSouth()) / 3, sx = (bb.getEast() - bb.getWest()) / 3; setVk(z >= 16 ? `${z}|${Math.round(c.lat / sy)}|${Math.round(c.lng / sx)}` : ''); };
    b(); map.on('zoomend moveend resize', b);
    return () => map.off('zoomend moveend resize', b);
  }, [mapRef, ready]);
  cb.current = { onFittingClick, onMoveFitting, onMoveVertex, onLineClick, onDeleteVertex, onPipeClick, onMovePipe };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!groupRef.current || groupRef.current.__map !== map) {
      groupRef.current = L.layerGroup().addTo(map);
      groupRef.current.__map = map;
    }
    const g = groupRef.current;
    g.clearLayers();
    const live = fittings.map((f) => ({ ...f }));
    const lines = [];
    const tip = (pp, pts) => `${esc(pp.name)} · ${esc(t['type_' + pp.type])}${pp.dn ? ' · ' + esc(pp.dn) : ''} · ${pathLength(pts).toFixed(1)} m`;

    pipes.forEach((pp) => {
      const pts = pipeLatLngs(pp, live);
      if (pts.length < 2) return;
      const color = cbColor(pp.color || PIPE_TYPES[pp.type] || PIPE_TYPES.other);
      const mt = pp.maint?.status, dim = !maintMatch(maintFilter, pp);
      const halo = L.polyline(pts, { pane: 'pipes', color: mt ? MAINT[mt] : '#ffffff', weight: mt ? 11 : 8, opacity: dim ? 0.15 : 0.85, interactive: false, className: mt ? `pipe-maint-${mt}` : '' });
      const line = L.polyline(pts, { pane: 'pipes', color, weight: pp.id === selId ? 7.5 : 4.5, opacity: dim ? 0.25 : 1, className: `pipe-line pipe-${pp.id}${pp.private ? ' pipe-private' : ''}${pp.plan ? ' pipe-plan' : ''}`, interactive: !draft, ...(pp.plan ? { dashArray: '10 7', opacity: 0.75 } : {}), ...(pp.private ? { dashArray: '9 7' } : {}) });
      if (pp.private) line.bindTooltip(`${esc(t.privShort)}: ${esc(pp.privLen || pathLength(pts).toFixed(2))} m`, { permanent: true, direction: 'center', className: 'priv-len-tip' });
      if (!pp.private) line.bindTooltip(tip(pp, pts), { sticky: true });
      line.on('click', (e) => { L.DomEvent.stopPropagation(e); if (netUnlocked) cb.current.onLineClick?.(pp.id, e.latlng); else cb.current.onPipeClick?.(pp.id, e.latlng); });
      g.addLayer(halo); g.addLayer(line);
      if (pp.type === 'power' || pp.type === 'telecom') g.addLayer(L.marker(pathMid(pts), { interactive: false, icon: L.divIcon({ className: 'cable-badge-wrap', html: `<span class="cable-badge cable-${safeKey(pp.type)}" data-testid="cable-badge-${safeKey(pp.id)}">${pp.type === 'power' ? '⚡' : '🌐'}</span>`, iconSize: [20, 20], iconAnchor: [10, 10] }) }));
      if (pp.type === 'sewer') sewerMarks(g, pp, pts, live);
      lines.push({ pp, halo, line, pts: pp.points.map((p) => (Array.isArray(p) ? [...p] : p)) });
    });
    const refresh = (pipeId) => lines.filter((l) => !pipeId || l.pp.id === pipeId).forEach((l) => {
      const pts = pipeLatLngs({ points: l.pts }, live);
      l.halo.setLatLngs(pts); l.line.setLatLngs(pts);
      l.line.setTooltipContent(tip(l.pp, pts));
    });

    if (draft?.kind === 'pipe' && draft.points.length) {
      const pts = pipeLatLngs(draft, fittings);
      const color = cbColor(draft.color || PIPE_TYPES[draft.type] || PIPE_TYPES.other);
      g.addLayer(L.polyline(pts, { pane: 'pipes', color, weight: 4, dashArray: '8 6', className: 'pipe-draft', interactive: false }));
      if (draft.type === 'sewer' && pts.length > 1) sewerMarks(g, { ...draft, id: 'draft' }, pts, fittings);
      pts.forEach((p, i) => g.addLayer(L.circleMarker(p, { pane: 'pipes', radius: i ? 5 : 7, color: '#fff', weight: 2, fillColor: color, fillOpacity: 1, interactive: false, className: 'draft-vertex' })));
    }

    const editing = netUnlocked && !draft;
    const large = live.length > 300, grid = new Map(), cell = (la, lo) => `${Math.round(la * 50000)},${Math.round(lo * 50000)}`;
    live.forEach((f) => { const c = cell(f.lat, f.lon); grid.set(c, (grid.get(c) || 0) + 1); });
    const vb = large && vk ? map.getBounds().pad(0.6) : null;
    live.forEach((f) => {
      if (vb && !editing && f.id !== selId && !vb.contains([f.lat, f.lon])) return;
      if (large && !editing && f.id !== selId) {
        if (f.tap && zb < 2) return;
        if ((f.type === 'house' || f.tap) && zb < 1) {
          const sz = f.tap ? 6 : 10;
          const dot = L.marker([f.lat, f.lon], { zIndexOffset: 800, icon: L.divIcon({ className: 'fit-lite-wrap', html: `<i class="fit-lite fit-lite-${safeKey(f.type)}" data-testid="map-lite-${safeKey(f.id)}" style="background:${fitColor(f)}"></i>`, iconSize: [sz, sz], iconAnchor: [sz / 2, sz / 2] }) });
          dot.bindTooltip(() => esc(f.name || ''), { direction: 'top' });
          dot.on('click', () => cb.current.onFittingClick?.(f.id));
          g.addLayer(dot);
          return;
        }
      }
      const match = (!fitFilter?.length || fitFilter.includes(f.state || 'none')) && maintMatch(maintFilter, f);
      const k = Math.cos((f.lat * Math.PI) / 180), n = grid.get(cell(f.lat, f.lon)) > 1 ? live.filter((o) => Math.hypot((o.lat - f.lat) * 111320, (o.lon - f.lon) * 111320 * k) < 0.3).length : 1;
      const stack = n > 1 ? `<i class="fit-stack" data-testid="stack-badge-${safeKey(f.id)}">${n}</i>` : '';
      const big = isRot(f);
      const icon = L.divIcon({ html: fittingHtml(t, f, press?.nodes[f.id], autoAngle(f, pipes, live), big ? fitArms(f, pipes, live) : null) + stack, className: `fit-wrap ${f.plan ? 'fit-plan' : ''} ${editing ? 'draggable' : ''} ${fitFilter?.length || maintFilter?.length ? (match ? 'fit-hit' : 'fit-dim') : ''}`, iconSize: big ? [44, 44] : [26, 26], iconAnchor: big ? [22, 22] : [13, 13] });
      const m = L.marker([f.lat, f.lon], { icon, draggable: editing || (f.id === selId && !draft), interactive: draft?.kind !== 'fitting', zIndexOffset: 500 });
      m.bindTooltip(`${esc(f.name)}${f.reduction ? ' · ' + esc(redText(f, t)) : ''}${f.privUnknown ? ' · ⚠️ ' + esc(t.privUnknownShort) : ''}${f.maint ? ' · ' + esc(t['mt_' + f.maint.status]) : ''}${f.state && f.state !== 'none' ? ' · ' + (f.scenReal ? `${esc(t.aqScenShort)}: ` : '') + esc(t['st_' + f.state]) + (f.scenReal ? ` (${esc(t.aqRealShort)}: ${esc(t['st_' + f.scenReal] || '?')})` : '') : ''}${!press?.nodes[f.id] && rawBar(f) != null ? ` · ${rawBar(f).toFixed(1).replace('.', ',')} bar (${esc(t.prRawHint)})` : ''}${f.note ? ' · ' + esc(f.note) : ''}`, { direction: 'top', offset: [0, -12] });
      m.on('click', () => cb.current.onFittingClick?.(f.id));
      m.on('drag', (e) => { const ll = e.target.getLatLng(); f.lat = ll.lat; f.lon = ll.lng; refresh(); });
      m.on('dragend', (e) => { const ll = e.target.getLatLng(); cb.current.onMoveFitting(f.id, ll.lat, ll.lng); });
      g.addLayer(m);
    });

    const selLine = !draft && lines.find((l) => l.pp.id === selId);
    if (selLine) {
      const mid = pathMid(pipeLatLngs({ points: selLine.pts }, live));
      const base = selLine.pts.map((p) => (Array.isArray(p) ? [...p] : p));
      const h = L.marker(mid, { draggable: true, zIndexOffset: 1200, icon: L.divIcon({ className: 'pit-handle-wrap', html: '<span class="pit-handle move" data-testid="pipe-move-handle"></span>', iconSize: [22, 22], iconAnchor: [11, 11] }) });
      h.on('drag', (e) => { const ll = e.target.getLatLng(); const dLa = ll.lat - mid[0], dLo = ll.lng - mid[1]; selLine.pts = base.map((p) => (Array.isArray(p) ? [p[0] + dLa, p[1] + dLo] : p)); refresh(selLine.pp.id); });
      h.on('dragend', () => cb.current.onMovePipe?.(selLine.pp.id, selLine.pts));
      g.addLayer(h);
    }
    if (editing || selLine) {
      lines.filter((l) => editing || l === selLine).forEach((l) => l.pts.forEach((p, i) => {
        if (!Array.isArray(p)) return;
        const icon = L.divIcon({ html: `<span class="vertex-handle" data-testid="vertex-${safeKey(l.pp.id)}-${i}"></span>`, className: 'vertex-wrap', iconSize: [16, 16], iconAnchor: [8, 8] });
        const m = L.marker(p, { icon, draggable: true, pane: 'markerPane' });
        const box = L.DomUtil.create('div', 'vertex-pop');
        const sv = L.DomUtil.create('button', 'btn btn-dark btn-sm', box);
        sv.textContent = `📐 ${t.svyBtnShort}`;
        sv.setAttribute('data-testid', `vertex-survey-${l.pp.id}-${i}`);
        L.DomEvent.on(sv, 'click', (ev) => { L.DomEvent.stop(ev); m.closePopup(); openSurvey({ kind: 'vertex', pipeId: l.pp.id, i }); });
        const btn = L.DomUtil.create('button', 'btn btn-red btn-sm vertex-del-btn', box);
        btn.textContent = t.vertexDelete;
        btn.setAttribute('data-testid', `vertex-delete-${l.pp.id}-${i}`);
        L.DomEvent.on(btn, 'click', (ev) => { L.DomEvent.stop(ev); m.closePopup(); cb.current.onDeleteVertex?.(l.pp.id, i); });
        m.bindPopup(box, { closeButton: false, offset: [0, -6], autoPan: true, autoPanPaddingTopLeft: [10, 70] });
        m.on('drag', (e) => { const ll = e.target.getLatLng(); l.pts[i] = [ll.lat, ll.lng]; refresh(l.pp.id); });
        m.on('dragend', (e) => { const ll = e.target.getLatLng(); cb.current.onMoveVertex(l.pp.id, i, [ll.lat, ll.lng]); });
        g.addLayer(m);
      }));
    }
  }, [mapRef, ready, pipes, fittings, draft, netUnlocked, fitFilter, maintFilter, t, selId, press, symStyle, zb, vk]);
}
